import {
  Box,
  Button,
  FormControl,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { translate } from 'i18n';
import { getAppConfig } from 'config';
import type { RoiInputs, RoiResults } from '../utils/roiCalculations';

export default function EmailCapture({
  inputs,
  results,
}: {
  inputs: RoiInputs;
  results: RoiResults;
}) {
  const [email, setEmail] = useState('');
  const [clubName, setClubName] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = useCallback(async () => {
    if (!email) return;
    setStatus('loading');
    try {
      const res = await fetch(`${getAppConfig().apiUrl}/marketing/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          clubName: clubName || undefined,
          roiInputs: inputs,
          roiResults: results,
        }),
      });
      if (!res.ok) throw new Error();
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }, [email, clubName, inputs, results]);

  if (status === 'success') {
    return (
      <Box
        bg="green.50"
        border="1px solid"
        borderColor="green.200"
        borderRadius="xl"
        p={6}
        textAlign="center"
        w="100%"
      >
        <Text fontWeight="600" color="green.700">
          {translate('marketing.email_capture_success')}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="2xl"
      p={{ base: 6, md: 8 }}
      w="100%"
    >
      <VStack spacing={3} align="stretch">
        <Text fontWeight="700" fontSize="md" color="gray.800" textAlign="center">
          {translate('marketing.email_capture_title')}
        </Text>
        <Text fontSize="sm" color="gray.500" textAlign="center">
          {translate('marketing.email_capture_subtitle')}
        </Text>
        <HStack spacing={3} mt={2}>
          <FormControl>
            <Input
              type="email"
              placeholder={translate('marketing.email_capture_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              bg="gray.50"
              borderRadius="xl"
              size="md"
            />
          </FormControl>
          <FormControl>
            <Input
              placeholder={translate('marketing.email_capture_club_placeholder')}
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
              bg="gray.50"
              borderRadius="xl"
              size="md"
            />
          </FormControl>
        </HStack>
        <Button
          bg="brand.500"
          color="white"
          borderRadius="xl"
          fontWeight="700"
          size="md"
          _hover={{ bg: 'brand.600' }}
          onClick={handleSubmit}
          isLoading={status === 'loading'}
          isDisabled={!email}
        >
          {translate('marketing.email_capture_submit')}
        </Button>
        {status === 'error' && (
          <Text fontSize="xs" color="red.500" textAlign="center">
            {translate('marketing.email_capture_error')}
          </Text>
        )}
      </VStack>
    </Box>
  );
}
